import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight, Mic, Zap, BarChart3 } from "lucide-react";

import voiceMockup from "@/assets/voice-posting-mockup-v2.png.asset.json";



/**
 * Homepage — Voice Posting & Mobile Access section.
 * Copy rail on the left; the device composition (laptop, phone, floating
 * cards) is a single transparent PNG rendered over the dark background.
 */

const TEAL = "#02857E";
const TEAL_BRIGHT = "#00CFC4";
const TEAL_GLOW = "#17E5DD";

const FEATURES = [
  {
    icon: Mic,
    title: "Speak it naturally",
    body: ["Tell LaneRunner the lane, equipment,", "and details in plain language."],
  },
  {
    icon: Zap,
    title: "Get it live fast",
    body: ["Post to your private network or", "open marketplace in seconds."],
  },
  {
    icon: BarChart3,
    title: "Watch quotes come in",
    body: ["Compare responses, move quickly,", "and award with confidence."],
  },
];

/* ---------------------------------------------------------------- */
/* Section                                                           */
/* ---------------------------------------------------------------- */
const HomeLoadBoard: React.FC = () => (
  <section className="relative overflow-hidden bg-[#03100F] py-20 sm:py-28">
    {/* background: near-black base */}
    <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_120%_90%_at_78%_58%,#08292B_0%,#051A1B_42%,#020B0B_100%)]" />
    {/* teal floor glow under the devices */}
    <div
      className="pointer-events-none absolute bottom-[6%] left-[62%] h-[420px] w-[820px] -translate-x-1/2 rounded-full blur-[120px]"
      style={{ background: "radial-gradient(ellipse, rgba(0,207,196,0.30) 0%, rgba(0,207,196,0) 70%)" }}
    />
    {/* right-edge light streaks */}
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full"
      preserveAspectRatio="none"
      viewBox="0 0 1440 900"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="vp-streak" x1="0" x2="1">
          <stop offset="0%" stopColor={TEAL_BRIGHT} stopOpacity="0" />
          <stop offset="60%" stopColor={TEAL_GLOW} stopOpacity="0.55" />
          <stop offset="100%" stopColor={TEAL_BRIGHT} stopOpacity="0.05" />
        </linearGradient>
        <linearGradient id="vp-streak-soft" x1="0" x2="1">
          <stop offset="0%" stopColor={TEAL_GLOW} stopOpacity="0" />
          <stop offset="70%" stopColor={TEAL_GLOW} stopOpacity="0.22" />
          <stop offset="100%" stopColor={TEAL_GLOW} stopOpacity="0" />
        </linearGradient>
      </defs>
      {[40, 92, 150, 214, 286, 366, 452, 546].map((y, i) => (
        <path
          key={y}
          d={`M ${1120 + i * 14} ${y} C 1250 ${y - 40}, 1330 ${y + 34}, 1460 ${y - 14}`}
          fill="none"
          stroke="url(#vp-streak)"
          strokeWidth={i % 2 ? 0.8 : 1.6}
          opacity={0.9 - i * 0.06}
        />
      ))}
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <path
          key={i}
          d={`M 1460 ${300 + i * 70} C 1360 ${430 + i * 60}, 1300 ${600 + i * 40}, 1180 ${900}`}
          fill="none"
          stroke="url(#vp-streak-soft)"
          strokeWidth="1.1"
        />
      ))}
    </svg>

    <div className="relative mx-auto grid w-full max-w-[1540px] grid-cols-1 items-center gap-16 px-5 sm:px-8 lg:grid-cols-[minmax(0,36%)_minmax(0,64%)] lg:gap-6 xl:gap-10">
      {/* ------------------ LEFT: copy rail ------------------ */}
      <div className="relative z-20">
        <div className="flex items-center gap-2.5">
          <svg width="30" height="16" viewBox="0 0 30 16" aria-hidden="true">
            {[1, 5, 9, 13, 17, 21, 25].map((x, i) => {
              const h = [7, 13, 9, 16, 10, 13, 6][i];
              return <rect key={x} x={x} y={(16 - h) / 2} width="2.2" height={h} rx="1.1" fill={TEAL_BRIGHT} />;
            })}
          </svg>
          <span
            className="text-[13px] font-bold uppercase tracking-[0.16em]"
            style={{ color: TEAL_BRIGHT, textShadow: "0 0 18px rgba(0,207,196,0.55)" }}
          >
            Voice Posting &amp; Mobile Access
          </span>
        </div>

        <h2
          className="mt-6 text-[42px] font-extrabold italic leading-[0.98] tracking-[-0.025em] text-white sm:text-[52px] lg:text-[46px] xl:text-[56px]"
          style={{ textShadow: "0 6px 40px rgba(0,0,0,0.55)" }}
        >
          Post a load or
          <br />
          quote <span style={{ color: TEAL_BRIGHT, textShadow: "0 0 32px rgba(0,207,196,0.45)" }}>in seconds.</span>
        </h2>

        <p className="mt-6 max-w-[27rem] text-[16px] leading-[1.6] text-[#C4D2D2]">
          Use LaneRunner to post freight or submit a quote by voice — from desktop or mobile. Skip the
          typing, get your opportunity live fast, and watch qualified quotes come in through one clean
          workflow.
        </p>

        <ul className="mt-10 space-y-7">
          {FEATURES.map((f) => (
            <li key={f.title} className="flex gap-5">
              <span
                className="flex h-[56px] w-[56px] shrink-0 items-center justify-center rounded-[14px] border"
                style={{
                  borderColor: "rgba(0,207,196,0.45)",
                  background: "linear-gradient(160deg, rgba(0,207,196,0.13), rgba(0,207,196,0.03))",
                  boxShadow: "0 0 30px -10px rgba(0,207,196,0.85), inset 0 1px 0 rgba(255,255,255,0.12)",
                }}
              >
                <f.icon className="h-6 w-6" style={{ color: TEAL_BRIGHT }} />
              </span>
              <div className="pt-1">
                <p className="text-[19px] font-bold leading-tight text-white">{f.title}</p>
                <p className="mt-1.5 text-[15px] leading-[1.5] text-[#A5B6B6]">
                  {f.body[0]}
                  <br />
                  {f.body[1]}
                </p>
              </div>
            </li>
          ))}
        </ul>

        <div className="mt-10 flex flex-wrap items-center gap-4">
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2.5 rounded-[10px] px-6 py-[14px] text-[15.5px] font-bold text-white transition hover:brightness-110"
            style={{ backgroundColor: TEAL, boxShadow: "0 20px 44px -24px rgba(0,207,196,0.95)" }}
          >
            Get Started Free <ArrowRight className="h-[18px] w-[18px]" />
          </Link>
          <Link
            to="/load-board"
            className="inline-flex items-center gap-2 rounded-[10px] border px-6 py-[14px] text-[15.5px] font-bold transition hover:bg-[rgba(0,207,196,0.08)]"
            style={{ borderColor: "rgba(0,207,196,0.5)", color: TEAL_BRIGHT }}
          >
            Explore the Load Board <ChevronRight className="h-[18px] w-[18px]" />
          </Link>
        </div>
      </div>

      {/* ------------------ RIGHT: device composition (image) ------------------ */}
      <div className="pointer-events-none relative z-10 select-none">
        <img
          src={voiceMockup.url}
          alt="LaneRunner load and dispatch overview on a laptop with the mobile app and voice posting cards"
          width={1445}
          height={955}
          className="h-auto w-full max-w-none lg:w-[122%] lg:-translate-x-[14%] xl:w-[118%] xl:-translate-x-[12%]"
          loading="lazy"
          decoding="async"
        />
      </div>
    </div>
  </section>
);

export default HomeLoadBoard;
